import type {
  ShopperAiTagId,
  SupportTicketId,
} from "@redotech/effect-api-shared/ids/definitions";
import {
  MongoCustomerId,
  MongoUserId,
} from "@redotech/effect-api-shared/ids/mongo-ids";
import type {
  SupportTicketListItemResponse,
  SupportTicketPriority,
  SupportTicketSnoozeOptions,
} from "@redotech/effect-api-shared/models/shopper-ai/conversation/support-ticket";
import { useHandler } from "@redotech/react-util/hook";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { SupportPermissions } from "@redotech/redo-model/permissions";
import type { BoundAction } from "@redotech/redo-web/actions/action-definition";
import type { ActionId } from "@redotech/redo-web/actions/action-ids";
import { useActionScope } from "@redotech/redo-web/actions/action-scope-context";
import {
  conversationActionIds,
  type ConversationActionId,
} from "@redotech/redo-web/actions/conversation-actions/action-ids";
import {
  addTagScope,
  assignScope,
  priorityScope,
  snoozeScope,
} from "@redotech/redo-web/actions/conversation-actions/scope-trees";
import { reactQueryClient } from "@redotech/redo-web/rpc-query/react-query-config";
import { useMemo } from "react";
import { supportTicketCountsQueryKey } from "../hooks/use-support-ticket-counts";
import { useGlobalSupportTicketCollections } from "../providers/global-support-ticket-collections-context";
import { actionRegistry } from "./action-registry";

function invalidateCountsAfterDelay() {
  setTimeout(() => {
    void reactQueryClient.invalidateQueries({
      queryKey: supportTicketCountsQueryKey,
    });
    void reactQueryClient.invalidateQueries({
      queryKey: ["activeViewStatusCounts"],
    });
  }, 2000);
}

/**
 * A conversation action with a bound execute function.
 */
export type SupportTicketAction<TParams = void> = BoundAction<
  SupportTicketListItemResponse,
  ConversationActionId,
  TParams,
  SupportTicketId
>;

export interface AssignSupportTicketParams {
  assigneeId: MongoUserId | null;
}

export interface AddTagSupportTicketParams {
  tagIds: ShopperAiTagId[];
}

export interface ChangeCustomerSupportTicketParams {
  mongoCustomerId: MongoCustomerId | null;
}

export type SetPriorityParams = SupportTicketPriority | null;

export type SnoozeParams =
  | { duration: Exclude<SupportTicketSnoozeOptions, "custom"> }
  | { duration: "custom"; until: Date }
  | null;

function computeSnoozeUntil(
  option: Exclude<SupportTicketSnoozeOptions, "custom">,
): Date {
  const now = Date.now();
  switch (option) {
    case "1h":
      return new Date(now + 60 * 60 * 1000);
    case "4h":
      return new Date(now + 4 * 60 * 60 * 1000);
    case "24h":
      return new Date(now + 24 * 60 * 60 * 1000);
  }
}

/**
 * Hook that returns the close conversation action with a bound handler.
 * Includes permission check in isEnabled.
 */
export function useCloseSupportTicketAction(): SupportTicketAction {
  const collections = useGlobalSupportTicketCollections();
  const canClose = useHasPermission(SupportPermissions.CLOSE_CONVERSATION);

  const execute = useHandler((supportTicketId: SupportTicketId) => {
    collections.supportTicketsList.collection.update(
      supportTicketId,
      (draft) => {
        draft.status = "CLOSED";
      },
    );
    invalidateCountsAfterDelay();
  });

  const registryAction = actionRegistry[conversationActionIds.close];

  return useMemo(
    () => ({
      ...registryAction,
      execute,
      isEnabled: (context: SupportTicketListItemResponse) => {
        if (!canClose) {
          return false;
        }
        return registryAction.isEnabled?.(context) ?? true;
      },
    }),
    [execute, registryAction, canClose],
  );
}

/**
 * Hook that returns the reopen conversation action with a bound handler.
 * Includes permission check in isEnabled.
 */
export function useReopenSupportTicketAction(): SupportTicketAction {
  const collections = useGlobalSupportTicketCollections();
  const canClose = useHasPermission(SupportPermissions.CLOSE_CONVERSATION);

  const execute = useHandler((supportTicketId: SupportTicketId) => {
    collections.supportTicketsList.collection.update(
      supportTicketId,
      (draft) => {
        draft.status = "OPEN";
      },
    );
    invalidateCountsAfterDelay();
  });

  const registryAction = actionRegistry[conversationActionIds.reopen];

  return useMemo(
    () => ({
      ...registryAction,
      execute,
      isEnabled: (context: SupportTicketListItemResponse) => {
        if (!canClose) {
          return false;
        }
        return registryAction.isEnabled?.(context) ?? true;
      },
    }),
    [execute, registryAction, canClose],
  );
}

/**
 * Hook that returns the snooze conversation action with a bound handler.
 */
export function useSnoozeSupportTicketAction(): SupportTicketAction<SnoozeParams> {
  const collections = useGlobalSupportTicketCollections();

  const execute = useHandler(
    (supportTicketId: SupportTicketId, option: SnoozeParams) => {
      if (option === null) {
        // Clear snooze - reopen the conversation
        collections.supportTicketsList.collection.update(
          supportTicketId,
          (draft) => {
            draft.status = "OPEN";
            draft.snoozedUntil = null;
          },
        );
      } else {
        const snoozedUntil =
          option.duration === "custom"
            ? option.until
            : computeSnoozeUntil(option.duration);
        collections.supportTicketsList.collection.update(
          supportTicketId,
          (draft) => {
            draft.status = "SNOOZED";
            draft.snoozedUntil = snoozedUntil;
          },
        );
      }
      invalidateCountsAfterDelay();
    },
  );

  return useMemo(
    () => ({ ...actionRegistry[conversationActionIds.snooze], execute }),
    [execute],
  );
}

export function useSnoozeSupportTicketOptions(): readonly SupportTicketAction[] {
  const setSnooze = useSnoozeSupportTicketAction();

  return useMemo(
    () => [
      {
        ...actionRegistry[conversationActionIds.snooze1h],
        execute: (supportTicketId: SupportTicketId) =>
          setSnooze.execute(supportTicketId, { duration: "1h" }),
      },
      {
        ...actionRegistry[conversationActionIds.snooze4h],
        execute: (supportTicketId: SupportTicketId) =>
          setSnooze.execute(supportTicketId, { duration: "4h" }),
      },
      {
        ...actionRegistry[conversationActionIds.snooze24h],
        execute: (supportTicketId: SupportTicketId) =>
          setSnooze.execute(supportTicketId, { duration: "24h" }),
      },
      {
        ...actionRegistry[conversationActionIds.snoozeCustom],
        // custom is handled by the modal — this is intentionally a no-op
        execute: (_supportTicketId: SupportTicketId) => undefined,
      },
      {
        ...actionRegistry[conversationActionIds.snoozeClear],
        execute: (supportTicketId: SupportTicketId) =>
          setSnooze.execute(supportTicketId, null),
      },
    ],
    [setSnooze],
  );
}

/**
 * Hook that returns the mark read conversation action with a bound handler.
 */
export function useMarkReadSupportTicketAction(): SupportTicketAction {
  const collections = useGlobalSupportTicketCollections();

  const execute = useHandler((supportTicketId: SupportTicketId) => {
    collections.supportTicketsList.collection.update(
      supportTicketId,
      (draft) => {
        draft.readAt = new Date();
      },
    );
  });

  return useMemo(
    () => ({ ...actionRegistry[conversationActionIds.markRead], execute }),
    [execute],
  );
}

/**
 * Hook that returns the mark unread conversation action with a bound handler.
 */
export function useMarkUnreadSupportTicketAction(): SupportTicketAction {
  const collections = useGlobalSupportTicketCollections();

  const execute = useHandler((supportTicketId: SupportTicketId) => {
    collections.supportTicketsList.collection.update(
      supportTicketId,
      (draft) => {
        draft.readAt = null;
      },
    );
  });

  return useMemo(
    () => ({ ...actionRegistry[conversationActionIds.markUnread], execute }),
    [execute],
  );
}

/**
 * Hook that returns the archive conversation action with a bound handler.
 * Includes permission check in isEnabled.
 */
export function useArchiveSupportTicketAction(): SupportTicketAction {
  const collections = useGlobalSupportTicketCollections();
  const canArchive = useHasPermission(SupportPermissions.ARCHIVE_CONVERSATION);

  const execute = useHandler((supportTicketId: SupportTicketId) => {
    collections.supportTicketsList.collection.update(
      supportTicketId,
      (draft) => {
        draft.status = "ARCHIVED";
      },
    );
    invalidateCountsAfterDelay();
  });

  const registryAction = actionRegistry[conversationActionIds.archive];

  return useMemo(
    () => ({
      ...registryAction,
      execute,
      isEnabled: (context: SupportTicketListItemResponse) => {
        if (!canArchive) {
          return false;
        }
        return registryAction.isEnabled?.(context) ?? true;
      },
    }),
    [execute, registryAction, canArchive],
  );
}

/**
 * Hook that returns the delete conversation action with a bound handler.
 */
export function useDeleteSupportTicketAction(): SupportTicketAction {
  const collections = useGlobalSupportTicketCollections();

  const execute = useHandler((supportTicketId: SupportTicketId) => {
    collections.supportTicketsList.collection.update(
      supportTicketId,
      (draft) => {
        draft.status = "DELETED";
      },
    );
    invalidateCountsAfterDelay();
  });

  return useMemo(
    () => ({ ...actionRegistry[conversationActionIds.delete], execute }),
    [execute],
  );
}

/**
 * Hook that returns the set priority conversation action with a bound handler.
 */
export function useSetPrioritySupportTicketAction(): SupportTicketAction<SetPriorityParams> {
  const collections = useGlobalSupportTicketCollections();

  const execute = useHandler(
    (supportTicketId: SupportTicketId, priority: SetPriorityParams) => {
      collections.supportTicketsList.collection.update(
        supportTicketId,
        (draft) => {
          draft.priority = priority;
        },
      );
    },
  );

  return useMemo(
    () => ({ ...actionRegistry[conversationActionIds.setPriority], execute }),
    [execute],
  );
}

/**
 * Hook that returns all priority option actions with the value baked in.
 * These are void actions that can be executed directly without params.
 */
export function usePriorityOptionActions(): readonly SupportTicketAction[] {
  const setPriority = useSetPrioritySupportTicketAction();

  return useMemo(
    () => [
      {
        ...actionRegistry[conversationActionIds.priorityUrgent],
        execute: (supportTicketId: SupportTicketId) =>
          setPriority.execute(supportTicketId, "URGENT"),
      },
      {
        ...actionRegistry[conversationActionIds.priorityClear],
        execute: (supportTicketId: SupportTicketId) =>
          setPriority.execute(supportTicketId, null),
      },
    ],
    [setPriority],
  );
}

/**
 * Hook that returns the assign conversation action with a bound handler.
 * Note: This requires the assignee ID to be passed as a param.
 * The actual UI for selecting an assignee should be handled separately.
 */
export function useAssignSupportTicketAction(): SupportTicketAction<AssignSupportTicketParams> {
  const collections = useGlobalSupportTicketCollections();

  const execute = useHandler(
    (supportTicketId: SupportTicketId, params: AssignSupportTicketParams) => {
      collections.supportTicketsList.collection.update(
        supportTicketId,
        (draft) => {
          draft.mongoAssigneeId = params.assigneeId;
        },
      );
      invalidateCountsAfterDelay();
    },
  );

  return useMemo(
    () => ({ ...actionRegistry[conversationActionIds.assign], execute }),
    [execute],
  );
}

/**
 * Hook that returns the add tag conversation action with a bound handler.
 * Note: This requires tag IDs to be passed as a param.
 * The actual UI for selecting tags should be handled separately.
 */
export function useAddTagSupportTicketAction(): SupportTicketAction<AddTagSupportTicketParams> {
  const collections = useGlobalSupportTicketCollections();

  const execute = useHandler(
    (supportTicketId: SupportTicketId, params: AddTagSupportTicketParams) => {
      collections.supportTicketsList.collection.update(
        supportTicketId,
        (draft) => {
          draft.tagIds = params.tagIds;
        },
      );
      invalidateCountsAfterDelay();
    },
  );

  return useMemo(
    () => ({ ...actionRegistry[conversationActionIds.addTag], execute }),
    [execute],
  );
}

/**
 * Hook that returns the change customer conversation action with a bound handler.
 * Note: This requires the customer ID to be passed as a param.
 * The actual UI for selecting a customer should be handled separately.
 */
export function useChangeCustomerSupportTicketAction(): {
  execute: (
    supportTicketId: SupportTicketId,
    params: ChangeCustomerSupportTicketParams,
  ) => void;
} {
  const collections = useGlobalSupportTicketCollections();

  const execute = useHandler(
    (
      supportTicketId: SupportTicketId,
      params: ChangeCustomerSupportTicketParams,
    ) => {
      collections.supportTicketsList.collection.update(
        supportTicketId,
        (draft) => {
          draft.mongoCustomerId = params.mongoCustomerId;
          // Clear customer info optimistically; it will be refreshed on next load
          draft.customer = undefined;
        },
      );
      invalidateCountsAfterDelay();
    },
  );

  return useMemo(() => ({ execute }), [execute]);
}

/**
 * All bound conversation actions.
 */
export interface SupportTicketActions {
  close: SupportTicketAction;
  reopen: SupportTicketAction;
  markInProgress: SupportTicketAction;
  snooze: SupportTicketAction<SnoozeParams>;
  markRead: SupportTicketAction;
  markUnread: SupportTicketAction;
  archive: SupportTicketAction;
  delete: SupportTicketAction;
  setPriority: SupportTicketAction<SetPriorityParams>;
  assign: SupportTicketAction<AssignSupportTicketParams>;
  addTag: SupportTicketAction<AddTagSupportTicketParams>;
}

/**
 * Hook that returns all conversation actions with bound handlers.
 * Useful for command palette, action menus, etc.
 */
export function useSupportTicketActions(): SupportTicketActions {
  const close = useCloseSupportTicketAction();
  const reopen = useReopenSupportTicketAction();
  const markInProgress = useMarkInProgressSupportTicketAction();
  const snooze = useSnoozeSupportTicketAction();
  const markRead = useMarkReadSupportTicketAction();
  const markUnread = useMarkUnreadSupportTicketAction();
  const archive = useArchiveSupportTicketAction();
  const deleteAction = useDeleteSupportTicketAction();
  const setPriority = useSetPrioritySupportTicketAction();
  const assign = useAssignSupportTicketAction();
  const addTag = useAddTagSupportTicketAction();

  return useMemo(
    () => ({
      close,
      reopen,
      markInProgress,
      snooze,
      markRead,
      markUnread,
      archive,
      delete: deleteAction,
      setPriority,
      assign,
      addTag,
    }),
    [
      close,
      reopen,
      markInProgress,
      snooze,
      markRead,
      markUnread,
      archive,
      deleteAction,
      setPriority,
      assign,
      addTag,
    ],
  );
}

/**
 * Hook that returns the mark in progress conversation action with a bound handler.
 * Includes permission check in isEnabled.
 */
export function useMarkInProgressSupportTicketAction(): SupportTicketAction {
  const collections = useGlobalSupportTicketCollections();
  const canClose = useHasPermission(SupportPermissions.CLOSE_CONVERSATION);

  const execute = useHandler((supportTicketId: SupportTicketId) => {
    collections.supportTicketsList.collection.update(
      supportTicketId,
      (draft) => {
        draft.status = "IN_PROGRESS";
      },
    );
    invalidateCountsAfterDelay();
  });

  const registryAction = actionRegistry[conversationActionIds.markInProgress];

  return useMemo(
    () => ({
      ...registryAction,
      execute,
      isEnabled: (context: SupportTicketListItemResponse) => {
        if (!canClose) {
          return false;
        }
        return registryAction.isEnabled?.(context) ?? true;
      },
    }),
    [execute, registryAction, canClose],
  );
}

/**
 * Hook that returns the send message action with a bound handler.
 */
export function useSendMessageAction(
  onSend: () => void | Promise<void>,
): SupportTicketAction {
  const execute = useHandler(() => {
    void onSend();
  });

  return useMemo(
    () => ({ ...actionRegistry[conversationActionIds.sendMessage], execute }),
    [execute],
  );
}

/**
 * Hook that returns the delete draft action with a bound handler.
 */
export function useDeleteDraftAction(
  onDelete: () => void | Promise<void>,
): SupportTicketAction {
  const execute = useHandler(() => {
    void onDelete();
  });

  return useMemo(
    () => ({ ...actionRegistry[conversationActionIds.deleteDraft], execute }),
    [execute],
  );
}

/**
 * Hook that returns the reply action with a bound handler.
 */
export function useReplyAction(
  onReply: () => void | Promise<void>,
): SupportTicketAction {
  const execute = useHandler(() => {
    void onReply();
  });

  return useMemo(
    () => ({ ...actionRegistry[conversationActionIds.reply], execute }),
    [execute],
  );
}

/**
 * Hook that returns the reply all action with a bound handler.
 */
export function useReplyAllAction(
  onReplyAll: () => void | Promise<void>,
): SupportTicketAction {
  const execute = useHandler(() => {
    void onReplyAll();
  });

  return useMemo(
    () => ({ ...actionRegistry[conversationActionIds.replyAll], execute }),
    [execute],
  );
}

/**
 * Hook that returns the forward action with a bound handler.
 */
export function useForwardAction(
  onForward: () => void | Promise<void>,
): SupportTicketAction {
  const execute = useHandler(() => {
    void onForward();
  });

  return useMemo(
    () => ({ ...actionRegistry[conversationActionIds.forward], execute }),
    [execute],
  );
}

/**
 * Hook that returns the internal note action with a bound handler.
 */
export function useInternalNoteAction(
  onInternalNote: () => void | Promise<void>,
): SupportTicketAction {
  const execute = useHandler(() => {
    void onInternalNote();
  });

  return useMemo(
    () => ({ ...actionRegistry[conversationActionIds.internalNote], execute }),
    [execute],
  );
}

/**
 * Type-safe map of action IDs to execute handlers.
 * Handlers are bound to a specific conversation ID, so they take no arguments.
 */
export type SupportTicketActionHandlerMap = Partial<
  Record<ActionId, () => void>
>;

/**
 * Hook that returns a map of action IDs to handlers bound to a specific conversation.
 * Used by ScopeMenu and keyboard shortcut system to execute actions.
 */
export function useSupportTicketActionHandlers(
  supportTicketId: SupportTicketId,
  onMergeTicket?: () => void,
): SupportTicketActionHandlerMap {
  const {
    close,
    reopen,
    markInProgress,
    snooze,
    markRead,
    markUnread,
    archive,
    delete: deleteAction,
    setPriority,
  } = useSupportTicketActions();

  const { pushScope } = useActionScope();

  return useMemo(
    (): SupportTicketActionHandlerMap => ({
      // Direct actions
      [conversationActionIds.close]: () => close.execute(supportTicketId),
      [conversationActionIds.reopen]: () => reopen.execute(supportTicketId),
      [conversationActionIds.markInProgress]: () =>
        markInProgress.execute(supportTicketId),
      [conversationActionIds.markRead]: () => markRead.execute(supportTicketId),
      [conversationActionIds.markUnread]: () =>
        markUnread.execute(supportTicketId),
      [conversationActionIds.archive]: () => archive.execute(supportTicketId),
      [conversationActionIds.delete]: () =>
        deleteAction.execute(supportTicketId),

      [conversationActionIds.mergeTicket]: () => onMergeTicket?.(),

      [conversationActionIds.setPriority]: () => pushScope(priorityScope),
      [conversationActionIds.assign]: () => pushScope(assignScope),
      [conversationActionIds.addTag]: () => pushScope(addTagScope),
      [conversationActionIds.snooze]: () => pushScope(snoozeScope),

      [conversationActionIds.priorityUrgent]: () =>
        setPriority.execute(supportTicketId, "URGENT"),
      [conversationActionIds.priorityClear]: () =>
        setPriority.execute(supportTicketId, null),

      // Snooze options
      [conversationActionIds.snooze1h]: () =>
        snooze.execute(supportTicketId, { duration: "1h" }),
      [conversationActionIds.snooze4h]: () =>
        snooze.execute(supportTicketId, { duration: "4h" }),
      [conversationActionIds.snooze24h]: () =>
        snooze.execute(supportTicketId, { duration: "24h" }),
      // snoozeCustom is handled by the modal in SupportTicketActionsDropdown
      [conversationActionIds.snoozeCustom]: () => undefined,
      [conversationActionIds.snoozeClear]: () =>
        snooze.execute(supportTicketId, null),

      // Table navigation - these don't operate on a single conversation
      // They should be handled separately by the table component
    }),
    [
      supportTicketId,
      close,
      reopen,
      markInProgress,
      snooze,
      markRead,
      markUnread,
      archive,
      deleteAction,
      setPriority,
      pushScope,
      onMergeTicket,
    ],
  );
}
